import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, LinkV2, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { Filters } from './Filters';
import type { FilterFacet } from './Filter.types';
import { SearchBar } from '../Search/SearchBar';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useApplySearchFiltersToTemplates } from '../../useApplySearchFiltersToTemplates';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { animationTransition } from '@shared/utils/animationTransition';
import { Restart } from '@withjoy/joykit/icons';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import { useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { SearchBox } from '../Search/SearchBox';

const NoSearchResultsText = ({ paddingLeft, onClick }: { paddingLeft?: BoxProps['paddingLeft']; onClick?: () => void }) => {
  const { t } = useTranslation('stationery');

  return (
    <TextV2 paddingLeft={paddingLeft}>
      {t('noSearchResults')()}{' '}
      <span>
        <LinkV2
          onClick={onClick}
          textDecoration="none"
        >
          {t('clear')()}
        </LinkV2>
      </span>
    </TextV2>
  );
};

const ResetAllFiltersButton = ({ onClick, pillHeight = 40 }: { onClick: () => void; pillHeight?: number }) => {
  const { t } = useTranslation('stationery');
  const tFiltersDrawer = t('dashboard', 'designsGallery', 'filtersDrawer');

  return (
    <Box
      paddingLeft={6}
      paddingRight={{ _: 6, sm2: 8 }}
      marginLeft="auto"
      flex="none"
    >
      <Box
        onClick={onClick}
        backgroundColor="mono2"
        color="mono14"
        as="button"
        height={pillHeight}
        paddingX={5}
        borderRadius={24}
        transition={animationTransition('background-color')}
      >
        <Box
          as="span"
          display="inline-flex"
          borderRadius="inherit"
          alignItems="center"
          columnGap={3}
          typographyVariant="button1"
        >
          <Restart size={16} />
          <Box
            as="span"
            display={{ _: 'none', sm: 'inline-flex' }}
          >
            {tFiltersDrawer.resetCta()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const SearchAndFiltersContainer = (props: { filterFacets: FilterFacet[]; trailingAction?: React.ReactNode }) => {
  const { filterFacets, trailingAction } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { buttonInteracted } = useCardTelemetry();
  const { clearAllFilters, getTotalActiveFilterCount } = useDesignGalleryFiltersContext();

  const [isSearchV1Enabled] = useIsFeatureEnabled('printGalleryEnableSearch');
  const [isSearchV2Enabled] = useIsFeatureEnabled('printSearch');
  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');

  const { useSearchResults, clearSearch } = useApplySearchFiltersToTemplates();
  const searchResults = useSearchResults();
  const { searchError } = searchResults;

  const handleClearAllFilters = () => {
    buttonInteracted({ name: 'galleryResetFilters', activeFiltersCount: getTotalActiveFilterCount() });
    clearAllFilters();
  };

  const pillHeight = previewWithPhotoEnabled ? 48 : 40;

  // New filter-bar design (Figma) is gated behind the preview-with-photo flag: filters on the left,
  // search + preview button right-aligned, all at a consistent 40px height.
  if (previewWithPhotoEnabled) {
    return (
      <>
        <Flex
          alignItems="center"
          paddingLeft={{ _: 6, sm2: 6 }}
          paddingRight={{ _: 4, sm2: 14 }}
          flexWrap="wrap"
        >
          <Flex
            flex={1}
            paddingY="1px"
            alignItems="center"
            gap={2}
            overflowX="auto"
            minWidth={0}
          >
            <Filters
              facets={filterFacets}
              pillHeight={pillHeight}
            />
            {!isMobileOrTablet && searchError && <NoSearchResultsText onClick={clearSearch} />}
            {!!getTotalActiveFilterCount() && (
              <ResetAllFiltersButton
                onClick={handleClearAllFilters}
                pillHeight={pillHeight}
              />
            )}
          </Flex>
          {(isSearchV1Enabled || isSearchV2Enabled || trailingAction) && (
            <Flex
              flex="none"
              alignItems="center"
              gap={3}
              paddingLeft={{ _: 0, sm2: 3 }}
              paddingTop={{ _: 2, sm2: 0 }}
              width={{ _: '100%', sm2: 'auto' }}
            >
              {isSearchV2Enabled && <SearchBox compact />}
              {isSearchV1Enabled && <SearchBar />}
              {trailingAction}
            </Flex>
          )}
        </Flex>
        {isMobileOrTablet && searchError && (
          <NoSearchResultsText
            paddingLeft={['1.5rem', '2.5rem']}
            onClick={clearSearch}
          />
        )}
      </>
    );
  }

  // Original layout (flag off).
  return (
    <>
      <Flex
        paddingLeft={{ _: 6, sm2: 8 }}
        paddingRight={{ _: 4, sm2: 6 }}
        paddingY="1px"
        alignItems="center"
        gap={{ _: 16, sm2: 24 }}
        overflowX="auto"
      >
        {isSearchV2Enabled && <SearchBox />}
        <Filters facets={filterFacets} />
        {isSearchV1Enabled && <SearchBar />}
        {!isMobileOrTablet && searchError && <NoSearchResultsText onClick={clearSearch} />}
        {!!getTotalActiveFilterCount() && (
          <ResetAllFiltersButton
            onClick={handleClearAllFilters}
            pillHeight={pillHeight}
          />
        )}
      </Flex>
      {isMobileOrTablet && searchError && (
        <NoSearchResultsText
          paddingLeft={['1.5rem', '2.5rem']}
          onClick={clearSearch}
        />
      )}
    </>
  );
};
